import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Plus, Trash, PencilSimple, FloppyDisk, Car, X } from "@phosphor-icons/react";

export function ViaticosVehicles() {
  const { token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ plate: "", brand: "", model: "", year: "", color: "" });

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/viaticos/vehicles`, { headers });
      setVehicles(data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ plate: "", brand: "", model: "", year: "", color: "" }); setEditId(null); setShowForm(false); };

  const handleSave = async () => {
    if (!form.plate || !form.brand) { toast.error("Matricula y marca son requeridos"); return; }
    try {
      if (editId) {
        await axios.put(`${API}/viaticos/vehicles/${editId}`, form, { headers });
        toast.success("Vehiculo actualizado");
      } else {
        await axios.post(`${API}/viaticos/vehicles`, form, { headers });
        toast.success("Vehiculo creado");
      }
      resetForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este vehiculo?")) return;
    try {
      await axios.delete(`${API}/viaticos/vehicles/${id}`, { headers });
      toast.success("Eliminado");
      load();
    } catch { toast.error("Error"); }
  };

  const startEdit = (v) => {
    setForm({ plate: v.plate, brand: v.brand, model: v.model, year: v.year || "", color: v.color || "" });
    setEditId(v.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-4" data-testid="viaticos-vehicles">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Catalogo de Vehiculos</h1>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5" data-testid="vehicles-add-btn">
          <Plus size={16} weight="bold" /> Agregar
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="vehicles-form">
          <h3 className="font-semibold text-slate-800">{editId ? "Editar" : "Nuevo"} Vehiculo</h3>
          <div className="grid grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Matricula *</label>
              <input value={form.plate} onChange={e => setForm(p => ({ ...p, plate: e.target.value.toUpperCase() }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="ABC-123" data-testid="vehicle-plate" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Marca *</label>
              <input value={form.brand} onChange={e => setForm(p => ({ ...p, brand: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="Nissan" data-testid="vehicle-brand" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Modelo</label>
              <input value={form.model} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="NP300" data-testid="vehicle-model" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Año</label>
              <input value={form.year} onChange={e => setForm(p => ({ ...p, year: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="2024" data-testid="vehicle-year" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Color</label>
              <input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" placeholder="Blanco" data-testid="vehicle-color" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-1.5" data-testid="vehicle-save">
              <FloppyDisk size={16} /> Guardar
            </button>
            <button onClick={resetForm} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200 flex items-center gap-1.5">
              <X size={16} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Car size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay vehiculos registrados</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Matricula</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Marca</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Modelo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Año</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Color</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {vehicles.map(v => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-bold text-slate-800">{v.plate}</td>
                  <td className="px-4 py-3">{v.brand}</td>
                  <td className="px-4 py-3">{v.model}</td>
                  <td className="px-4 py-3">{v.year}</td>
                  <td className="px-4 py-3">{v.color}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => startEdit(v)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded" data-testid={`vehicle-edit-${v.id}`}><PencilSimple size={16} /></button>
                      <button onClick={() => handleDelete(v.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded" data-testid={`vehicle-delete-${v.id}`}><Trash size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
